import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { Select } from "components/Select/Select";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { PciI } from "interfaces/Pci";
import { PlanEstudioI } from "interfaces/PlanEstudio";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { getPcis } from "services/pci";
import { getPlanesEstudio } from "services/planesEstudio";
import { PCIRowActions } from "./PCITable/PCIRowActions";
import { PCITable } from "./PCITable/PCITable";
import {
  ImportacionMasivaModal,
  ImportacionMasivaTipo,
} from "./ImportacionMasivaModal";

export const PCI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<PciI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();
  const [planesEstudio, setPlanesEstudio] = useState<PlanEstudioI[]>([]);
  const [loadingPlanes, setLoadingPlanes] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [tipoImportacion, setTipoImportacion] =
    useState<ImportacionMasivaTipo>("pci");

  const IMPORTADOR_PCI = false;
  const IMPORTADOR_INSTITUCIONES = false;

  const navigate = useNavigate();

  const initialValues = {
    nombre: "",
    idPlanEstudio: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchPcis = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getPcis({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar los PCI");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (elemento: PciI) => {
    navigate(PRIVATE_ROUTE.PCI_EDITAR + "/" + elemento.id);
  };

  const handleCreate = () => {
    navigate(PRIVATE_ROUTE.PCI_CREAR);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchPcis({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchPcis();
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const refreshTable = () => {
    setPage(1);
    searchPcis({ filtros: { ...values, page: 1 } });
  };

  const handleOpenImportModal = (tipo: ImportacionMasivaTipo) => {
    setTipoImportacion(tipo);
    setShowImportModal(true);
  };

  const renderActions = (
    elemento: PciI,
    onClickShowDetail: (elemento: PciI) => void,
    onClickCompare: (elemento: PciI) => void,
  ) => {
    return (
      <PCIRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
        onClickShowDetail={onClickShowDetail}
        onClickCompare={onClickCompare}
      />
    );
  };

  useEffect(() => {
    searchPcis({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    const fetchPlanes = async () => {
      try {
        setLoadingPlanes(true);
        const response = await getPlanesEstudio({ limit: 100 });
        setPlanesEstudio(response.elementos);
      } catch (err) {
        showErrorToast("No fue posible cargar los planes de estudio");
      } finally {
        setLoadingPlanes(false);
      }
    };
    fetchPlanes();
  }, []);

  return (
    <section>
      <h1 className="section-title">PCI</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar los Proyectos Curriculares Institucionales.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar nombre"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Select
            id="idPlanEstudio"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={planesEstudio}
            isLoading={loadingPlanes}
            placeholder="Plan de estudio"
            width="200px"
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={!values.nombre && !values.idPlanEstudio}
          >
            Limpiar filtros
          </Button>
        </div>
        <div className="d-flex gap-2">
          {IMPORTADOR_PCI && (
            <Button
              size="lg"
              variant="secondary"
              onClick={() => handleOpenImportModal("pci")}
            >
              Importar PCI
            </Button>
          )}
          {IMPORTADOR_INSTITUCIONES && (
            <Button
              size="lg"
              variant="secondary"
              onClick={() => handleOpenImportModal("institucion")}
            >
              Importar instituciones
            </Button>
          )}
          <Button size="lg" onClick={handleCreate}>
            Nuevo PCI
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <PCITable elementos={elementos} renderActions={renderActions} />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}

      <ImportacionMasivaModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        tipo={tipoImportacion}
      />
    </section>
  );
};
